import { getCurrentScope, onScopeDispose, ref, watch, type Ref } from 'vue'

import { useColorScheme } from '@/composables/useColorScheme'

/**
 * Resolves a ui-lib semantic token (declared with `light-dark()`) to the concrete
 * colour for the scheme in effect on `context`.
 *
 * Only for third-party code that cannot read CSS variables (Chart.js, Mermaid,
 * iframe documents, `<meta name="theme-color">`). Everything rendered by our own
 * components uses `var(--token)` directly.
 *
 * `getPropertyValue('--token')` would return the unresolved `light-dark(...)`
 * string, so the token is applied to a hidden probe and its computed colour read.
 */
export const resolveThemeToken = (
  token: string,
  context: Element = document.documentElement,
): string => {
  const probe = document.createElement('span')
  probe.style.display = 'none'
  probe.style.color = `var(${token})`
  context.appendChild(probe)
  const value = getComputedStyle(probe).color
  probe.remove()
  return toRgbString(value)
}

let pixel: CanvasRenderingContext2D | null | undefined

/**
 * Tokens built with `color-mix()` compute to `color(srgb …)`, which some libraries
 * (Chart.js's colour parser among them) reject. Painting one canvas pixel
 * normalises any CSS colour to `rgb()` / `rgba()`.
 */
const toRgbString = (color: string): string => {
  if (!color || color.startsWith('rgb')) return color
  if (pixel === undefined) {
    pixel = document
      .createElement('canvas')
      .getContext('2d', { willReadFrequently: true })
  }
  if (!pixel) return color
  pixel.clearRect(0, 0, 1, 1)
  pixel.fillStyle = color
  pixel.fillRect(0, 0, 1, 1)
  const [r, g, b, a] = pixel.getImageData(0, 0, 1, 1).data
  return a === 255
    ? `rgb(${r}, ${g}, ${b})`
    : `rgba(${r}, ${g}, ${b}, ${Math.round((a / 255) * 1000) / 1000})`
}

/**
 * Reactive map of resolved token colours that updates after the colour scheme
 * changes. Pass the token names you need, e.g. `['--text-secondary', '--border-default']`.
 */
export const useThemeTokens = <T extends string>(
  tokens: readonly T[],
): Ref<Record<T, string>> => {
  const { colorScheme } = useColorScheme()

  const read = () =>
    Object.fromEntries(
      tokens.map((token) => [token, resolveThemeToken(token)]),
    ) as Record<T, string>

  const values = ref(read()) as Ref<Record<T, string>>

  // flush: 'post' so <html> already carries the new color-scheme when we read.
  const stop = watch(
    colorScheme,
    () => {
      values.value = read()
    },
    { flush: 'post' },
  )
  if (getCurrentScope()) onScopeDispose(stop)

  return values
}
